import React from 'react';
import { Card, CardProps, styled } from '@mui/material';
import { motion, HTMLMotionProps } from 'framer-motion';

interface AnimatedCardProps extends Omit<CardProps, 'ref'> {
  delay?: number;
  duration?: number;
  hover?: boolean;
  glow?: boolean;
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'glow',
})<{ glow?: boolean }>(({ theme, glow }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s',
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[10],
    ...(glow && {
      boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)',
    }),
    '&::before': {
      transform: 'translateX(100%)',
    },
  },
}));

const MotionCard = motion.create(StyledCard as any);

const AnimatedCard: React.FC<AnimatedCardProps> = ({
  children,
  delay = 0,
  duration = 0.5,
  hover = true,
  glow = false,
  ...props
}) => {
  const motionProps: HTMLMotionProps<'div'> = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration,
      delay,
      ease: [0.48, 0.15, 0.25, 0.96],
    },
  };

  if (hover) {
    motionProps.whileHover = {
      scale: 1.02,
      transition: { duration: 0.2 },
    };
    motionProps.whileTap = {
      scale: 0.98,
    };
  }

  return (
    <MotionCard glow={glow} {...motionProps} {...(props as any)}>
      {children}
    </MotionCard>
  );
};

export default AnimatedCard;